/**
 * Detects when the client is out of date, and how badly (Workstream V).
 *
 * Returns a TIER, not a boolean:
 *   'soft' → dismissible UpdateBanner (suggests)
 *   'hard' → blocking ForceUpdateGate (enforces)
 *
 * Native: reads the NATIVE app version via @capawesome/capacitor-app-update
 * (`CFBundleShortVersionString` / `versionName`) and compares it to the Remote
 * Config floors. It deliberately does NOT use the baked-in `APP_VERSION`, which
 * is the JS bundle's version — a Capgo OTA bundle can move that past a floor
 * while the native shell stays stale, laundering a device through the gate.
 * The native read is what makes this gate OTA-proof.
 *
 * The same call tells us whether the store actually HAS a newer build, which
 * the wall requires — see the failure ladder in lib/versionGate.ts.
 *
 * Web: polls /version.json for a new deploy. An ordinary deploy goes 'soft'
 * (dismissible banner). When the deploy also carries a `forceReloadBelow` floor
 * the running bundle fails, the web "wall" is a FORCED RELOAD — scheduled via
 * lib/webForceReload.ts, which defers while a Stripe/Plaid surface is open so
 * nobody is trapped mid-checkout. The reported tier stays 'soft' on web (the
 * banner shows during any deferral); 'hard' remains native-only, so the
 * ForceUpdateGate wall in App/index.tsx never renders on web.
 */

import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';

import { BUILD_HASH, APP_VERSION } from '../version';
import { useRemoteConfig } from '../context/RemoteConfigContext';
import { resolveGateTier, resolveWebGate, type GateTier } from '../lib/versionGate';
import { scheduleForcedReload, getLastForcedReloadTarget } from '../lib/webForceReload';
import { logWarn } from '../lib/log';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

export interface VersionCheck {
  tier: GateTier;
  isNative: boolean;
  platform: string;
  /** The native version the device is actually running. '' on web. */
  currentVersion: string;
  /** The floor it failed, for the gate's support footnote. '' when not gated. */
  requiredVersion: string;
}

export function useVersionCheck(): VersionCheck {
  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();
  const {
    minIosVersion,
    minAndroidVersion,
    forceUpdateBelowIos,
    forceUpdateBelowAndroid,
  } = useRemoteConfig();

  const [tier, setTier] = useState<GateTier>('none');
  const [currentVersion, setCurrentVersion] = useState('');
  const [requiredVersion, setRequiredVersion] = useState('');

  const isIos = platform === 'ios';
  const softFloor = isIos ? minIosVersion : minAndroidVersion;
  const hardFloor = isIos ? forceUpdateBelowIos : forceUpdateBelowAndroid;

  // Native: native version vs. the RC floors.
  useEffect(() => {
    if (!isNative) return;
    // Nothing configured — skip the plugin call entirely.
    if (!softFloor && !hardFloor) {
      setTier('none');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const info = await AppUpdate.getAppUpdateInfo();
        if (cancelled) return;

        const next = resolveGateTier({
          currentVersion: info.currentVersionName,
          softFloor,
          hardFloor,
          updateAvailableInStore:
            info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE,
        });

        setCurrentVersion(info.currentVersionName);
        setRequiredVersion(next === 'hard' ? hardFloor : softFloor);
        setTier(next);
      } catch (error) {
        // The store lookup can fail (offline, store unreachable, sideloaded
        // build). Stay inert rather than guess: we cannot confirm an update
        // exists, and a wall the user cannot satisfy is worse than no wall.
        if (cancelled) return;
        logWarn('[versionCheck] app update info unavailable', error);
        setTier('none');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isNative, softFloor, hardFloor]);

  // Web: poll version.json for a new deploy. The UI tier is only ever 'soft'
  // (the banner); a failed forceReloadBelow floor arms the guarded forced
  // reload instead of a wall — see the header comment.
  useEffect(() => {
    if (isNative) return;

    let timer: ReturnType<typeof setInterval>;

    const check = async () => {
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const decision = resolveWebGate({
          currentVersion: APP_VERSION,
          floor: typeof data.forceReloadBelow === 'string' ? data.forceReloadBelow : '',
          deployedBuildHash: typeof data.buildHash === 'string' ? data.buildHash : '',
          runningBuildHash: BUILD_HASH,
          alreadyAttemptedFor: getLastForcedReloadTarget(),
        });
        if (decision === 'none') return;
        setTier('soft');
        setCurrentVersion(APP_VERSION);
        clearInterval(timer);
        if (decision === 'reload') {
          // Reloads now if safe; defers past open Stripe/Plaid surfaces
          // (retry interval + visibilitychange) with the banner up meanwhile.
          scheduleForcedReload(data.buildHash);
        }
      } catch {
        // Network error — skip this check
      }
    };

    const initialTimeout = setTimeout(check, 30_000);
    timer = setInterval(check, POLL_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(timer);
    };
  }, [isNative]);

  return { tier, isNative, platform, currentVersion, requiredVersion };
}
